import { useEffect, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Search, SlidersHorizontal, ChevronLeft, ChevronRight, X } from 'lucide-react';
import api from '../lib/api';
import ProfileCard from '../components/ProfileCard';

const RELIGIONS = ['Hindu', 'Muslim', 'Christian', 'Sikh', 'Buddhist', 'Jain'];
const DIETS = ['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan'];

export default function SearchPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    gender: user?.gender === 'male' ? 'female' : 'male',
    religion: '', caste: '', city: '', state: '',
    age_min: '', age_max: '', education: '', diet: '',
    verified_only: false,
  });

  const fetchProfiles = async (pg = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== '' && v !== false) params.append(k, v);
      });
      params.append('page', pg);
      params.append('limit', 12);
      const res = await api.get(`/profiles/search/all?${params}`);
      setProfiles(res.data.profiles || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setPage(pg);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfiles(); }, []);

  const handleSearch = (e) => { e?.preventDefault(); fetchProfiles(1); };
  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => {
    setFilters({ gender: user?.gender === 'male' ? 'female' : 'male', religion: '', caste: '', city: '', state: '', age_min: '', age_max: '', education: '', diet: '', verified_only: false });
  };

  const FilterPanel = () => (
    <div className="space-y-5">
      <div>
        <Label className="text-sm font-medium">{t('register.gender')}</Label>
        <Select value={filters.gender} onValueChange={(v) => updateFilter('gender', v)}>
          <SelectTrigger className="mt-1.5 h-10 rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="male">Male</SelectItem>
            <SelectItem value="female">Female</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">{t('search.age_range')} Min</Label>
          <Input type="number" value={filters.age_min} onChange={(e) => updateFilter('age_min', e.target.value)} placeholder="18" className="mt-1 h-9 text-sm" data-testid="filter-age-min" />
        </div>
        <div>
          <Label className="text-xs">Max</Label>
          <Input type="number" value={filters.age_max} onChange={(e) => updateFilter('age_max', e.target.value)} placeholder="40" className="mt-1 h-9 text-sm" data-testid="filter-age-max" />
        </div>
      </div>
      <div>
        <Label className="text-sm font-medium">{t('register.religion')}</Label>
        <Select value={filters.religion} onValueChange={(v) => updateFilter('religion', v)}>
          <SelectTrigger className="mt-1.5 h-10 rounded-lg"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any_religion">Any</SelectItem>
            {RELIGIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-sm font-medium">{t('register.caste')}</Label>
        <Input value={filters.caste} onChange={(e) => updateFilter('caste', e.target.value)} placeholder="Any" className="mt-1.5 h-10" data-testid="filter-caste" />
      </div>
      <div>
        <Label className="text-sm font-medium">{t('register.city')}</Label>
        <Input value={filters.city} onChange={(e) => updateFilter('city', e.target.value)} placeholder="Any city" className="mt-1.5 h-10" data-testid="filter-city" />
      </div>
      <div>
        <Label className="text-sm font-medium">{t('register.diet')}</Label>
        <Select value={filters.diet} onValueChange={(v) => updateFilter('diet', v)}>
          <SelectTrigger className="mt-1.5 h-10 rounded-lg"><SelectValue placeholder="Any" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="any_diet">Any</SelectItem>
            {DIETS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">{t('search.verified_only')}</Label>
        <Switch checked={filters.verified_only} onCheckedChange={(v) => updateFilter('verified_only', v)} data-testid="filter-verified" />
      </div>
      <div className="flex gap-2">
        <Button onClick={handleSearch} className="flex-1 bg-[#D9381E] hover:bg-[#B91C1C] text-white rounded-full" data-testid="apply-filters-btn">
          {t('search.apply')}
        </Button>
        <Button variant="outline" onClick={() => { clearFilters(); }} className="rounded-full border-[#E7E5E4]" data-testid="clear-filters-btn">
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="search-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#2D2424]">{t('search.title')}</h1>
          <p className="text-sm text-[#78716C] mt-1">{total} {t('search.results')}</p>
        </div>
        <Sheet>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="outline" className="rounded-full gap-2 border-[#E7E5E4]" data-testid="mobile-filter-btn">
              <SlidersHorizontal className="w-4 h-4" /> {t('search.filters')}
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80">
            <SheetHeader><SheetTitle>{t('search.filters')}</SheetTitle></SheetHeader>
            <div className="mt-6"><FilterPanel /></div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="flex gap-8">
        {/* Desktop Sidebar */}
        <div className="hidden lg:block w-72 flex-shrink-0">
          <Card className="p-5 rounded-xl border border-[#E7E5E4] bg-white sticky top-24">
            <h3 className="font-semibold text-[#2D2424] mb-4 flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" /> {t('search.filters')}
            </h3>
            <FilterPanel />
          </Card>
        </div>

        {/* Results */}
        <div className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-3 border-[#D9381E] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : profiles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {profiles.map(p => <ProfileCard key={p.id} profile={p} />)}
              </div>
              {pages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-8">
                  <Button variant="outline" onClick={() => fetchProfiles(page - 1)} disabled={page <= 1} className="rounded-full">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-[#78716C]">Page {page} of {pages}</span>
                  <Button variant="outline" onClick={() => fetchProfiles(page + 1)} disabled={page >= pages} className="rounded-full">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            <Card className="p-16 text-center rounded-xl border border-[#E7E5E4]">
              <Search className="w-12 h-12 text-[#E7E5E4] mx-auto mb-4" />
              <p className="text-[#78716C]">{t('search.no_results')}</p>
              <Button onClick={clearFilters} variant="outline" className="mt-4 rounded-full">{t('search.clear_filters')}</Button>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
